import type { ImageRef, Release } from "./types";

// Releases are listed in hand-curated display order — the array order is the
// order shown on /releases (Coming soon block, then Released block) and on the
// home page teasers. Spotlight is whichever entry has `spotlight: true` —
// currently SWEET OUTSIDE (pre-release).
//
// Cover keys: SWEET OUTSIDE points at the already-uploaded variants. All other
// releases point at `public/artwork/<slug>/cover-3000.jpg` — these will 404
// until Alex drops a source image at ASSETS/Artwork/releases/<slug>.{jpg,png}
// and runs `npm run generate-covers -- --upload`. The MediaImage component
// onError-falls-back to the clean Diamond Cat in the meantime.

/**
 * Build an ImageRef for a release cover. `slug` is the source-image slug
 * (which may be shared across multiple releases when the source is an EP
 * cover used by all its tracks). `title` is purely for the alt text.
 *
 * The variants list mirrors what scripts/generate-release-covers.mjs emits.
 * MediaImage falls back to the clean Diamond Cat on load error, so entries
 * whose source images don't exist yet still render gracefully.
 */
function cover(slug: string, title: string): ImageRef {
  return {
    ref: { kind: "public", key: `public/artwork/${slug}/cover-3000.jpg` },
    alt: `${title} — cover art`,
    width: 3000,
    height: 3000,
    variants: [
      { key: `public/artwork/${slug}/cover-1200.avif`, format: "avif", width: 1200 },
      { key: `public/artwork/${slug}/cover-1200.webp`, format: "webp", width: 1200 },
      { key: `public/artwork/${slug}/cover-800.avif`, format: "avif", width: 800 },
      { key: `public/artwork/${slug}/cover-800.webp`, format: "webp", width: 800 },
      { key: `public/artwork/${slug}/cover-400.avif`, format: "avif", width: 400 },
      { key: `public/artwork/${slug}/cover-400.webp`, format: "webp", width: 400 },
    ],
  };
}

/**
 * Build an ImageRef for a release backdrop. Variants mirror what
 * scripts/generate-release-covers.mjs emits under
 * public/artwork/<slug>/bg-{800,1600,2400}.{webp,avif}. Use when a release
 * has a dedicated background image source.
 */
function releaseBackground(slug: string, title: string): ImageRef {
  return {
    ref: { kind: "public", key: `public/artwork/${slug}/bg-2400.webp` },
    alt: `${title} — backdrop`,
    width: 2400,
    height: 2400,
    variants: [
      { key: `public/artwork/${slug}/bg-2400.avif`, format: "avif", width: 2400 },
      { key: `public/artwork/${slug}/bg-2400.webp`, format: "webp", width: 2400 },
      { key: `public/artwork/${slug}/bg-1600.avif`, format: "avif", width: 1600 },
      { key: `public/artwork/${slug}/bg-1600.webp`, format: "webp", width: 1600 },
      { key: `public/artwork/${slug}/bg-800.avif`, format: "avif", width: 800 },
      { key: `public/artwork/${slug}/bg-800.webp`, format: "webp", width: 800 },
    ],
  };
}

// SWEET OUTSIDE's cover doubles as the full-bleed backdrop on its detail page
// (see ReleaseBackdrop). There's no dedicated bg-* source for it — the square
// cover art *is* the intended backdrop, exactly like STIMULATE, whose own
// backdrop source is also a square image. Shared between `cover` and
// `background` below so the two can never drift apart.
const sweetOutsideCover: ImageRef = {
  ref: { kind: "public", key: "public/artwork/sweet-outside/cover-3000.jpg" },
  alt: "SWEET OUTSIDE — cover art",
  width: 3000,
  height: 3000,
  variants: [
    { key: "public/artwork/sweet-outside/cover-1200.avif", format: "avif", width: 1200 },
    { key: "public/artwork/sweet-outside/cover-1200.webp", format: "webp", width: 1200 },
    { key: "public/artwork/sweet-outside/cover-800.avif", format: "avif", width: 800 },
    { key: "public/artwork/sweet-outside/cover-800.webp", format: "webp", width: 800 },
    { key: "public/artwork/sweet-outside/cover-400.avif", format: "avif", width: 400 },
    { key: "public/artwork/sweet-outside/cover-400.webp", format: "webp", width: 400 },
  ],
};

export const releases: readonly Release[] = [
  // --- Coming soon (display order). Pre-release SWEET OUTSIDE first
  //     (spotlight), then the announced/TBA queue in artist-preferred order. ---
  {
    slug: "sweet-outside",
    title: "SWEET OUTSIDE",
    artistCredit: "7 Minutes Dead",
    label: "mau5trap",
    releaseDate: "2026-06-12",
    status: "pre-release",
    spotlight: true,
    cover: sweetOutsideCover,
    // Full-bleed cover art behind the hero, same treatment as STIMULATE.
    background: sweetOutsideCover,
    description:
      "UK Garage at Techno BPM. Coming via our friends at mau5trap. Let's go.",
    artistQuote:
      "I've been listening to ZEET NOISE and \"hit me where it hurts\" on repeat, but people have been pressuring me to make DNB, so a DNB rhythm at 150bpm is what my heart settled on. I should probably make more of these.",
    tracks: [
      {
        position: 1,
        title: "SWEET OUTSIDE",
        durationSec: 155,
        releaseStatus: "forthcoming",
        master: {
          ref: { kind: "private", key: "private/audio/unreleased/sweet-outside/sweet-outside.wav" },
          peaksRef: { kind: "public", key: "public/audio/peaks/sweet-outside.json" },
          durationSec: 155,
          mime: "audio/wav",
          markers: [
            { label: "Build", atSec: 52 },
            { label: "Drop", atSec: 77 },
            { label: "Switch", atSec: 101 },
            { label: "Outro", atSec: 129 },
          ],
        },
        preview: {
          ref: { kind: "public", key: "public/audio/previews/sweet-outside.wav" },
          peaksRef: { kind: "public", key: "public/audio/peaks/sweet-outside-clip.json" },
          durationSec: 67,
          mime: "audio/wav",
          markers: [
            { label: "Build", atSec: 40 },
            { label: "Drop", atSec: 65 },
          ],
        },
      },
    ],
    pressLinks: [],
    dsps: [],
    videos: [],
  },
  {
    slug: "pwah",
    title: "PWAH",
    artistCredit: "7 Minutes Dead",
    label: "TBA",
    status: "announced",
    spotlight: false,
    cover: cover("pwah", "PWAH"),
    description: "",
    liveSlugs: ["pwah-live-left-to-right"],
    tracks: [
      {
        position: 1,
        title: "PWAH",
        durationSec: 169,
        releaseStatus: "demo",
        master: {
          ref: { kind: "private", key: "private/audio/vault/pwah/preview.wav" },
          peaksRef: { kind: "public", key: "public/audio/peaks/pwah.json" },
          durationSec: 169,
          mime: "audio/wav",
        },
        preview: {
          ref: { kind: "public", key: "public/audio/previews/pwah.wav" },
          peaksRef: { kind: "public", key: "public/audio/peaks/pwah-clip.json" },
          durationSec: 57,
          mime: "audio/wav",
        },
      },
    ],
  },
  {
    slug: "soliloquy",
    title: "SOLILOQUY",
    artistCredit: "7 Minutes Dead",
    label: "TBA",
    status: "announced",
    spotlight: false,
    cover: cover("soliloquy", "SOLILOQUY"),
    description: "",
    liveSlugs: ["its-a-beat-like-soliloquy-live"],
    tracks: [
      {
        position: 1,
        title: "SOLILOQUY",
        durationSec: 301,
        releaseStatus: "demo",
        master: {
          ref: { kind: "private", key: "private/audio/vault/soliloquy/preview.mp3" },
          durationSec: 301,
          mime: "audio/mpeg",
        },
        preview: {
          ref: { kind: "public", key: "public/audio/previews/soliloquy.wav" },
          peaksRef: { kind: "public", key: "public/audio/peaks/soliloquy-clip.json" },
          durationSec: 71,
          mime: "audio/wav",
        },
      },
    ],
  },
  {
    slug: "if-you-love-me",
    title: "IF YOU LOVE ME",
    artistCredit: "7 Minutes Dead",
    label: "TBA",
    status: "announced",
    spotlight: false,
    cover: cover("if-you-love-me", "IF YOU LOVE ME"),
    description: "",
    liveSlugs: ["if-you-love-me-live"],
    tracks: [
      {
        position: 1,
        title: "IF YOU LOVE ME",
        durationSec: 154,
        releaseStatus: "demo",
        master: {
          ref: { kind: "private", key: "private/audio/vault/if-you-love-me/preview.wav" },
          peaksRef: { kind: "public", key: "public/audio/peaks/if-you-love-me.json" },
          durationSec: 154,
          mime: "audio/wav",
        },
        preview: {
          ref: { kind: "public", key: "public/audio/previews/if-you-love-me.wav" },
          peaksRef: { kind: "public", key: "public/audio/peaks/if-you-love-me-clip.json" },
          durationSec: 44,
          mime: "audio/wav",
        },
      },
    ],
  },
  {
    slug: "my-only-one",
    title: "MY ONLY ONE",
    artistCredit: "7 Minutes Dead",
    label: "TBA",
    status: "announced",
    spotlight: false,
    cover: cover("my-only-one", "MY ONLY ONE"),
    description: "Instrumental preview — full version pending.",
    tracks: [
      {
        position: 1,
        title: "MY ONLY ONE",
        durationSec: 557,
        releaseStatus: "demo",
        master: {
          ref: { kind: "private", key: "private/audio/vault/my-only-one/preview.mp3" },
          durationSec: 557,
          mime: "audio/mpeg",
        },
        preview: {
          ref: { kind: "public", key: "public/audio/previews/my-only-one.wav" },
          peaksRef: { kind: "public", key: "public/audio/peaks/my-only-one-clip.json" },
          durationSec: 118,
          mime: "audio/wav",
        },
      },
    ],
  },
  {
    slug: "be-my-killa",
    title: "BE MY KILLA",
    artistCredit: "7 Minutes Dead",
    label: "TBA",
    status: "announced",
    spotlight: false,
    cover: cover("be-my-killa", "BE MY KILLA"),
    description: "",
    tracks: [
      {
        position: 1,
        title: "BE MY KILLA",
        durationSec: 0,
        releaseStatus: "demo",
        master: {
          ref: { kind: "private", key: "private/audio/vault/be-my-killa/preview.wav" },
          peaksRef: { kind: "public", key: "public/audio/peaks/be-my-killa.json" },
          durationSec: 0,
          mime: "audio/wav",
        },
      },
    ],
  },
  {
    slug: "where-the-heart-is",
    title: "WHERE THE HEART IS",
    artistCredit: "7 Minutes Dead",
    label: "TBA",
    status: "announced",
    spotlight: false,
    cover: cover("where-the-heart-is", "WHERE THE HEART IS"),
    description: "",
    tracks: [
      {
        position: 1,
        title: "WHERE THE HEART IS",
        durationSec: 0,
        releaseStatus: "demo",
        master: {
          ref: { kind: "private", key: "private/audio/vault/where-the-heart-is/preview.wav" },
          peaksRef: { kind: "public", key: "public/audio/peaks/where-the-heart-is.json" },
          durationSec: 0,
          mime: "audio/wav",
        },
      },
    ],
  },

  // --- Released back-catalog (display order — strongest/most-recent first per
  //     artist preference, not strictly chronological). Spotify embed URLs
  //     wired so the EPK doesn't have to host audio for released items. ---
  {
    slug: "stimulate",
    title: "STIMULATE",
    artistCredit: "7 Minutes Dead",
    label: "Self-released",
    releaseDate: "2024-04-22",
    status: "released",
    spotlight: false,
    cover: cover("stimulate", "STIMULATE"),
    background: releaseBackground("stimulate", "STIMULATE"),
    spotifyEmbedUrl: "https://open.spotify.com/track/6IuclbYiepYW0wB8XoM0Lo",
    description: "",
    liveSlugs: ["stimulate-rapture-mash-up"],
    tracks: [
      {
        position: 1,
        title: "STIMULATE",
        durationSec: 148,
        releaseStatus: "released",
        master: {
          ref: { kind: "private", key: "private/audio/vault/stimulate/stimulate.wav" },
          peaksRef: { kind: "public", key: "public/audio/peaks/stimulate.json" },
          durationSec: 148,
          mime: "audio/wav",
        },
      },
    ],
  },
  {
    slug: "we-do",
    title: "We Do",
    artistCredit: "7 Minutes Dead",
    label: "Monstercat",
    releaseDate: "2017-05-29",
    status: "released",
    spotlight: false,
    // Part of ORBITER EP.
    cover: cover("orbiter-ep", "We Do"),
    spotifyEmbedUrl: "https://open.spotify.com/track/0ldQ4g120XuAOgwWkBKWKZ",
    description: "",
    tracks: [
      {
        position: 1,
        title: "We Do",
        durationSec: 0,
        releaseStatus: "released",
        master: { ref: { kind: "external", url: "" }, durationSec: 0, mime: "audio/mpeg" },
      },
    ],
  },
  {
    slug: "without-chu",
    title: "Without Chu",
    artistCredit: "7 Minutes Dead",
    label: "Monstercat",
    releaseDate: "2019-01-08",
    status: "released",
    spotlight: false,
    cover: cover("without-chu", "Without Chu"),
    spotifyEmbedUrl: "https://open.spotify.com/track/3ZyTo6kNv71EW6AUEdvEAd",
    description: "",
    tracks: [
      {
        position: 1,
        title: "Without Chu",
        durationSec: 0,
        releaseStatus: "released",
        master: { ref: { kind: "external", url: "" }, durationSec: 0, mime: "audio/mpeg" },
      },
    ],
  },
  {
    slug: "vibin-without-chu",
    title: "Vibin Without Chu",
    artistCredit: "7 Minutes Dead",
    label: "Self-released",
    releaseDate: "2020-12-15",
    status: "released",
    spotlight: false,
    cover: cover("vibin-without-chu", "Vibin Without Chu"),
    spotifyEmbedUrl: "https://open.spotify.com/track/5LiTh0Pead94zoeVfUA2GS",
    description: "",
    tracks: [
      {
        position: 1,
        title: "Vibin Without Chu",
        durationSec: 0,
        releaseStatus: "released",
        master: { ref: { kind: "external", url: "" }, durationSec: 0, mime: "audio/mpeg" },
      },
    ],
  },
  {
    slug: "its-ok-if-you-wanna",
    title: "It's OK If You Wanna",
    artistCredit: "7 Minutes Dead",
    label: "Self-released",
    releaseDate: "2024-04-22",
    status: "released",
    spotlight: false,
    // Shares the STIMULATE cover (same release week, same self-release).
    cover: cover("stimulate", "It's OK If You Wanna"),
    spotifyEmbedUrl: "https://open.spotify.com/track/3cpDqCtyyr5onMqmtCYsMX",
    description: "",
    tracks: [
      {
        position: 1,
        title: "It's OK If You Wanna",
        durationSec: 0,
        releaseStatus: "released",
        master: {
          ref: { kind: "external", url: "" },
          durationSec: 0,
          mime: "audio/mpeg",
        },
      },
    ],
  },
  {
    slug: "mirai-sekai-pt-1-neo-seoul",
    title: "Mirai Sekai Pt.1: Neo-Seoul",
    artistCredit: "7 Minutes Dead",
    label: "Monstercat",
    releaseDate: "2014-09-01",
    status: "released",
    spotlight: false,
    cover: cover("mirai-sekai-ep", "Mirai Sekai Pt.1: Neo-Seoul"),
    spotifyEmbedUrl: "https://open.spotify.com/track/6KDUFBRme0t12piagzbRf8",
    description: "",
    tracks: [
      {
        position: 1,
        title: "Mirai Sekai Pt.1: Neo-Seoul",
        durationSec: 0,
        releaseStatus: "released",
        master: { ref: { kind: "external", url: "" }, durationSec: 0, mime: "audio/mpeg" },
      },
    ],
  },
  {
    slug: "mirai-sekai-pt-2-neo-tokyo",
    title: "Mirai Sekai Pt.2: Neo-Tokyo",
    artistCredit: "7 Minutes Dead",
    label: "Monstercat",
    releaseDate: "2014-09-01",
    status: "released",
    spotlight: false,
    cover: cover("mirai-sekai-ep", "Mirai Sekai Pt.2: Neo-Tokyo"),
    spotifyEmbedUrl: "https://open.spotify.com/track/7tgqRZPTGfGdj5p1UwzhjB",
    description: "",
    tracks: [
      {
        position: 1,
        title: "Mirai Sekai Pt.2: Neo-Tokyo",
        durationSec: 0,
        releaseStatus: "released",
        master: { ref: { kind: "external", url: "" }, durationSec: 0, mime: "audio/mpeg" },
      },
    ],
  },
  {
    slug: "mirai-sekai-pt-3-aeon-metropolis",
    title: "Mirai Sekai Pt.3: Aeon Metropolis",
    artistCredit: "7 Minutes Dead",
    label: "Monstercat",
    releaseDate: "2014-09-01",
    status: "released",
    spotlight: false,
    cover: cover("mirai-sekai-ep", "Mirai Sekai Pt.3: Aeon Metropolis"),
    spotifyEmbedUrl: "https://open.spotify.com/track/69GM8yiK14d9VwmHfEJha8",
    description: "",
    tracks: [
      {
        position: 1,
        title: "Mirai Sekai Pt.3: Aeon Metropolis",
        durationSec: 0,
        releaseStatus: "released",
        master: { ref: { kind: "external", url: "" }, durationSec: 0, mime: "audio/mpeg" },
      },
    ],
  },
  {
    slug: "mirai-sekai-pt-4-goodnight-sequence",
    title: "Mirai Sekai Pt.4: Goodnight_Sequence",
    artistCredit: "7 Minutes Dead",
    label: "Monstercat",
    releaseDate: "2014-09-01",
    status: "released",
    spotlight: false,
    cover: cover("mirai-sekai-ep", "Mirai Sekai Pt.4: Goodnight_Sequence"),
    spotifyEmbedUrl: "https://open.spotify.com/track/5HdIie0QQ5sqdrq2SRcP9X",
    description: "",
    tracks: [
      {
        position: 1,
        title: "Mirai Sekai Pt.4: Goodnight_Sequence",
        durationSec: 0,
        releaseStatus: "released",
        master: { ref: { kind: "external", url: "" }, durationSec: 0, mime: "audio/mpeg" },
      },
    ],
  },
  {
    slug: "mirai-sekai-continuous-mix",
    title: "Mirai Sekai (Continuous Mix)",
    artistCredit: "7 Minutes Dead",
    label: "Monstercat",
    releaseDate: "2014-09-01",
    status: "released",
    spotlight: false,
    cover: cover("mirai-sekai-ep", "Mirai Sekai (Continuous Mix)"),
    spotifyEmbedUrl: "https://open.spotify.com/track/6GnoxbkPzqSFqvfXtosJ1W",
    description: "",
    tracks: [
      {
        position: 1,
        title: "Mirai Sekai (Continuous Mix)",
        durationSec: 0,
        releaseStatus: "released",
        master: { ref: { kind: "external", url: "" }, durationSec: 0, mime: "audio/mpeg" },
      },
    ],
  },
  {
    slug: "the-divide",
    title: "The Divide",
    artistCredit: "7 Minutes Dead",
    label: "Monstercat",
    releaseDate: "2015-02-22",
    status: "released",
    spotlight: false,
    cover: cover("the-divide", "The Divide"),
    spotifyEmbedUrl: "https://open.spotify.com/track/4T5rbcUkLe9TrMwwaNdFuX",
    description: "",
    tracks: [
      {
        position: 1,
        title: "The Divide",
        durationSec: 0,
        releaseStatus: "released",
        master: { ref: { kind: "external", url: "" }, durationSec: 0, mime: "audio/mpeg" },
      },
    ],
  },
  // --- Remainder, in original chronological order. ---
  {
    slug: "want-you-back",
    title: "Want You Back",
    artistCredit: "7 Minutes Dead",
    label: "Self-released",
    releaseDate: "2022-10-03",
    status: "released",
    spotlight: false,
    cover: cover("want-you-back", "Want You Back"),
    spotifyEmbedUrl: "https://open.spotify.com/track/4Rv6gr6X53IipLwien7wsI",
    description: "",
    tracks: [
      {
        position: 1,
        title: "Want You Back",
        durationSec: 0,
        releaseStatus: "released",
        master: { ref: { kind: "external", url: "" }, durationSec: 0, mime: "audio/mpeg" },
      },
    ],
  },
  {
    slug: "discovery",
    title: "Discovery",
    artistCredit: "7 Minutes Dead",
    label: "Monstercat",
    releaseDate: "2017-05-29",
    status: "released",
    spotlight: false,
    // Part of ORBITER EP.
    cover: cover("orbiter-ep", "Discovery"),
    spotifyEmbedUrl: "https://open.spotify.com/track/4FNvq6XgyaMYUj0G3OuU9R",
    description: "",
    tracks: [
      {
        position: 1,
        title: "Discovery",
        durationSec: 0,
        releaseStatus: "released",
        master: { ref: { kind: "external", url: "" }, durationSec: 0, mime: "audio/mpeg" },
      },
    ],
  },
  {
    slug: "want-you",
    title: "Want You",
    artistCredit: "7 Minutes Dead",
    label: "Monstercat",
    releaseDate: "2017-05-29",
    status: "released",
    spotlight: false,
    cover: cover("want-you", "Want You"),
    spotifyEmbedUrl: "https://open.spotify.com/track/3gYo5JHVnludip2fnFeVAn",
    description: "",
    tracks: [
      {
        position: 1,
        title: "Want You",
        durationSec: 0,
        releaseStatus: "released",
        master: { ref: { kind: "external", url: "" }, durationSec: 0, mime: "audio/mpeg" },
      },
    ],
  },
  {
    slug: "sidewinder",
    title: "Sidewinder",
    artistCredit: "7 Minutes Dead",
    label: "Monstercat",
    releaseDate: "2013-04-10",
    status: "released",
    spotlight: false,
    // Part of the 7 Minutes Dead EP.
    cover: cover("7-minutes-dead-ep", "Sidewinder"),
    spotifyEmbedUrl: "https://open.spotify.com/track/3eKXsXfqqpOKEj6l8iLMGY",
    description: "",
    tracks: [
      {
        position: 1,
        title: "Sidewinder",
        durationSec: 0,
        releaseStatus: "released",
        master: { ref: { kind: "external", url: "" }, durationSec: 0, mime: "audio/mpeg" },
      },
    ],
  },
  {
    slug: "the-follower",
    title: "The Follower",
    artistCredit: "7 Minutes Dead",
    label: "Monstercat",
    releaseDate: "2013-04-10",
    status: "released",
    spotlight: false,
    // Part of the 7 Minutes Dead EP.
    cover: cover("7-minutes-dead-ep", "The Follower"),
    spotifyEmbedUrl: "https://open.spotify.com/track/0LXtE3GcSn8uQesLUrKN9C",
    description: "",
    tracks: [
      {
        position: 1,
        title: "The Follower",
        durationSec: 0,
        releaseStatus: "released",
        master: { ref: { kind: "external", url: "" }, durationSec: 0, mime: "audio/mpeg" },
      },
    ],
  },
];

export function getRelease(slug: string): Release | undefined {
  return releases.find((r) => r.slug === slug);
}

export function getSpotlight(): Release | undefined {
  return releases.find((r) => r.spotlight);
}

export function isComingSoon(r: Release): boolean {
  return r.status === "pre-release" || r.status === "announced";
}

export function isReleased(r: Release): boolean {
  return r.status === "released";
}

/** Whether the release has anything publicly listenable on the EPK side. */
export function hasPublicAudio(r: Release): boolean {
  return r.tracks.some(
    (t) => t.preview && t.preview.ref.kind !== "external" || (t.preview?.ref.kind === "external" && t.preview.ref.url),
  );
}
